class ProductCheckboxOptions extends HTMLElement {
  constructor() {
    super();
    this.form = document.getElementById(this.dataset.formId);
    this.checkboxes = this.querySelectorAll('input[type="checkbox"]');
    this.selectedProducts = new Map();
    this.accessoriesTotalContainer = this.querySelector('.accessories-total');
    this.accessoriesTotalAmount = this.querySelector('.accessories-total-amount');
    this.totalWithAccessories = this.querySelector('.total-with-accessories');
    this.totalWithAccessoriesAmount = this.querySelector('.total-with-accessories-amount');
    this.priceElement = document.querySelector('.price__sale .price-item--sale:not(.variant-item__old-price), .price__regular .price-item--regular, .main-price-wrapper .price-item--sale, .main-price-wrapper .price-item--regular');
    this.currentMainPrice = 0;
    this.currency = 'USD'; // Default currency, will be updated from Shopify
    this.init();
  }

  init() {
    // Initialize current main price
    this.updateMainPrice();
    
    // Add event listeners to checkboxes
    this.checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', this.handleCheckboxChange.bind(this));
    });

    // Listen for form submission
    if (this.form) {
      this.form.addEventListener('submit', this.handleFormSubmit.bind(this));
    }

    // Listen for variant changes
    const variantSelectors = this.form ? this.form.querySelectorAll('input[name="id"], select[name="id"]') : [];
    variantSelectors.forEach(select => {
      select.addEventListener('change', this.handleVariantChange.bind(this));
    });

    // Initialize price observer
    this.initPriceObserver();
    
    // Initial update
    this.updateAccessoriesTotal();
  }

  initPriceObserver() {
    // First, try to find the price element if not already set
    if (!this.priceElement) {
      this.priceElement = this.findPriceElement();
    }

    // Set up observer for price changes
    if (this.priceElement) {
      // Disconnect any existing observer to avoid duplicates
      if (this.priceObserver) {
        this.priceObserver.disconnect();
      }

      // Create observer instance
      this.priceObserver = new MutationObserver((mutations) => {
        let shouldUpdate = false;
        
        mutations.forEach((mutation) => {
          // Check if the price text content changed or if the price element itself was added/removed
          if (mutation.type === 'characterData' || 
              (mutation.type === 'childList' && 
               (this.isPriceElement(mutation.target) || 
                Array.from(mutation.addedNodes).some(node => this.isPriceElement(node))))) {
            shouldUpdate = true;
          }
        });
        
        if (shouldUpdate) {
          console.log('Price change detected, updating calculations...');
          this.updateMainPrice();
        }
      });

      // Find the closest stable parent to observe
      const parentToObserve = this.priceElement.closest('.price__container') || 
                             this.priceElement.parentNode || 
                             document.body;
      
      // Start observing the parent container for changes
      this.priceObserver.observe(parentToObserve, {
        characterData: true,
        childList: true,
        subtree: true
      });
      
      console.log('Price observer initialized on:', parentToObserve);
    } else {
      console.warn('Price element not found, will retry on next interaction');
      // Fallback: Try to find the price element again on next interaction
      document.addEventListener('click', this.handleDocumentClick.bind(this), { once: true });
    }
  }

  handleDocumentClick() {
    // Try to initialize the price observer again on next interaction
    if (!this.priceElement) {
      this.initPriceObserver();
    }
  }

  updateMainPrice() {
    try {
      // If price element is not set, try to find it
      if (!this.priceElement) {
        this.priceElement = this.findPriceElement();
        if (!this.priceElement) {
          console.warn('Price element not found');
          return;
        }
      }

      // Get the price text and clean it up
      let priceText = this.priceElement.textContent.trim();
      
      // Log the raw price text for debugging
      console.log('Raw price text:', priceText);
      
      // Extract numeric value (handles various currency formats)
      const priceMatch = priceText.match(/[\d.,]+/);
      if (!priceMatch) {
        console.warn('Could not extract price from:', priceText);
        return;
      }
      
      // Normalize decimal separator and convert to cents to avoid floating point issues
      const priceValue = parseFloat(priceMatch[0].replace(/[^0-9.]/g, ''));
      if (isNaN(priceValue)) {
        console.warn('Invalid price value:', priceMatch[0]);
        return;
      }
      
      this.currentMainPrice = Math.round(priceValue * 100);
      console.log('Updated main price to:', this.currentMainPrice / 100);
      
      // Update the accessories total with the new main price
      this.updateAccessoriesTotal();
    } catch (error) {
      console.error('Error updating main price:', error);
    }
  }

  handleVariantChange() {
    // Small delay to allow the price to update in the DOM
    setTimeout(() => {
      // Re-find the price element in case it was replaced
      this.priceElement = this.findPriceElement();
      if (this.priceElement) {
        console.log('Variant changed, updating price...');
        this.updateMainPrice();
      } else {
        console.warn('Could not find price element after variant change');
        // Try to reinitialize the observer
        this.initPriceObserver();
      }
    }, 100);
  }

  handleCheckboxChange(event) {
    const checkbox = event.target;
    const productId = checkbox.value;
    const variantId = checkbox.dataset.variantId;
    const price = parseInt(checkbox.dataset.price || '0', 10);
    const quantity = checkbox.checked ? 1 : 0;

    // Store the selected state in our map
    this.selectedProducts.set(productId, { 
      quantity, 
      variantId,
      price
    });
    
    // Update the form's hidden input for the selected product
    this.updateHiddenInputs(productId, variantId, quantity);
    
    // Update the accessories total
    this.updateAccessoriesTotal();
  }

  updateHiddenInputs(productId, variantId, quantity) {
    // Add quantity input
    let quantityInput = this.form.querySelector(`input[name="items[${productId}][quantity]"]`);
    
    if (quantity === 0 && quantityInput) {
      // Remove the input if quantity is 0 and input exists
      quantityInput.remove();
      
      // Also remove variant input if it exists
      const variantInput = this.form.querySelector(`input[name="items[${productId}][id]"]`);
      if (variantInput) variantInput.remove();
      
      return;
    }
    
    // If the hidden input doesn't exist, create it
    if (!quantityInput) {
      quantityInput = document.createElement('input');
      quantityInput.type = 'hidden';
      quantityInput.name = `items[${productId}][quantity]`;
      this.form.appendChild(quantityInput);
    }
    
    quantityInput.value = quantity;
    
    // Add variant ID input
    let variantInput = this.form.querySelector(`input[name="items[${productId}][id]"]`);
    
    if (!variantInput) {
      variantInput = document.createElement('input');
      variantInput.type = 'hidden';
      variantInput.name = `items[${productId}][id]`;
      this.form.appendChild(variantInput);
    }
    
    variantInput.value = variantId;
  }

  updateAccessoriesTotal() {
    let accessoriesTotal = 0;
    let hasSelectedAccessories = false;
    
    // Calculate total price of selected accessories
    this.checkboxes.forEach(checkbox => {
      if (checkbox.checked) {
        const price = parseInt(checkbox.dataset.price || '0', 10);
        if (!isNaN(price)) {
          accessoriesTotal += price;
          hasSelectedAccessories = true;
        }
      }
    });
    
    // Toggle main price container visibility in both top and bottom sections
    const mainPriceContainers = document.querySelectorAll('.main-price-wrapper .price__container');
    mainPriceContainers.forEach(container => {
      container.style.display = hasSelectedAccessories ? 'none' : 'block';
    });
    
    // Show the price-with-accessories container when accessories are selected
    const priceWithAccessories = document.querySelectorAll('.price-with-accessories');
   /* if (priceWithAccessories) {
      priceWithAccessories.style.display = hasSelectedAccessories ? 'block' : 'none';
    }*/

    priceWithAccessories.forEach( priceWithAccessory => {
      priceWithAccessory.style.display = hasSelectedAccessories ? 'block' : 'none';
    });
    
    // Calculate totals
    const totalWithAccessories = this.currentMainPrice + accessoriesTotal;
    const discountAmount = hasSelectedAccessories ? Math.round(totalWithAccessories * 0.05) : 0; // 5% discount
    const discountedTotal = totalWithAccessories - discountAmount;
    
    // Format prices
    const formattedAccessoriesPrice = this.formatPrice(accessoriesTotal);
    const formattedOriginalTotal = this.formatPrice(totalWithAccessories);
    const formattedDiscountedTotal = this.formatPrice(discountedTotal);
    const formattedDiscount = this.formatPrice(discountAmount);
    
    // Update all price displays on the page
    const priceContainers = document.querySelectorAll('.price-with-accessories .total-with-accessories');
    
    priceContainers.forEach(container => {
      const amountElement = container.querySelector('.total-with-accessories-amount');
      if (amountElement) {
        if (hasSelectedAccessories && discountAmount > 0) {
          amountElement.innerHTML = `
            <span class="original-price" style="text-decoration: line-through; opacity: 0.7; margin-right: 5px;">${formattedOriginalTotal}</span>
            <span class="discounted-price">${formattedDiscountedTotal}</span>
            <div class="discount-badge" style="display: inline-block; color: #f44336; padding: 2px 6px; font-size: 12px; margin-left: 5px;">
              You Saved 5%
            </div>
          `;
        } else {
          amountElement.textContent = formattedOriginalTotal;
        }
        container.style.display = 'block';
      }
    });
    
    // Hide the accessories-total-container in both top and bottom sections
    const accessoriesTotalContainers = document.querySelectorAll('.accessories-total-container');
    accessoriesTotalContainers.forEach(container => {
      container.style.display = 'none';
    });
  }

  formatPrice(priceInCents) {
    // Use Shopify's currency formatting if available, otherwise default to USD
    const locale = window.Shopify && window.Shopify.locale ? window.Shopify.locale : 'en-US';
    const currency = window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'USD';
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(priceInCents / 100);
  }

  handleFormSubmit(event) {
    // Prevent the default form submission
    event.preventDefault();
    
    // When the form is submitted (Add to Cart button clicked)
    // Make sure all selected products are included in the form data
    this.selectedProducts.forEach((data, productId) => {
      if (data.quantity > 0) {
        this.updateHiddenInputs(productId, data.variantId, data.quantity);
      }
    });
    
    // Now manually submit the form
    this.form.submit();
  }

  // Helper method to find the price element
  findPriceElement() {
    return document.querySelector('.price__sale .price-item--sale:not(.variant-item__old-price), .price__regular .price-item--regular, .main-price-wrapper .price-item--sale, .main-price-wrapper .price-item--regular');
  }

  // Helper method to check if an element is a price element
  isPriceElement(element) {
    if (!element || !element.classList) return false;
    
    // Check if it's a price element by class
    const isPriceElement = element.classList.contains('price-item--sale') || 
                          element.classList.contains('price-item--regular');
    
    // Make sure it's not a compare-at price
    const isNotComparePrice = !element.closest('.variant-item__old-price');
    
    return isPriceElement && isNotComparePrice;
  }
}

customElements.define('product-checkbox-options', ProductCheckboxOptions);